import {
  HEARTBEAT_INTERVAL_SECONDS,
  type UserAgentHints,
} from "@pax/api-contract/activity/v1alpha1";
import { api } from "@/lib/api";
import { tabSessionId } from "@/lib/telemetry";

const DEVICE_ID_STORAGE_KEY = "pax.activity.device_id";
const INPUT_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"] as const;

/** Chromium's `navigator.userAgentData`, which TypeScript's DOM types do not describe yet. */
interface UserAgentData {
  getHighEntropyValues(hints: string[]): Promise<{
    fullVersionList?: { brand: string; version: string }[];
    platform?: string;
    platformVersion?: string;
    mobile?: boolean;
    model?: string;
  }>;
}

let lastInputAt: number | undefined;
let liveGameId: string | null = null;

/** One id per browser, kept across tabs and visits so signed-out play is counted. */
function deviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing) return existing;
  const created = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_STORAGE_KEY, created);
  return created;
}

/** The browser and OS versions Chromium reports on request; other browsers have none. */
async function userAgentHints(): Promise<UserAgentHints | null> {
  const data = (navigator as Navigator & { userAgentData?: UserAgentData }).userAgentData;
  if (!data) return null;
  const values = await data
    .getHighEntropyValues(["fullVersionList", "platformVersion", "model"])
    .catch(() => null);
  if (!values) return null;
  return {
    brands: values.fullVersionList ?? [],
    platform: values.platform ?? "",
    platformVersion: values.platformVersion ?? "",
    mobile: values.mobile ?? false,
    model: values.model ?? "",
  };
}

/** Marks the tab as holding a live socket to the game until the returned function runs. */
export function reportLiveGame(gameId: string): () => void {
  liveGameId = gameId;
  return () => {
    if (liveGameId === gameId) liveGameId = null;
  };
}

/**
 * Sends a heartbeat every 30 seconds while the tab is visible. Play sessions are measured from
 * these on the server, so each one says what the tab shows, whether anyone is engaged with it,
 * and what it runs on. The server adds who the player is and the network they connect from.
 */
export async function startActivityHeartbeats(
  currentScreen: () => { screen: string; gameId: string | null },
): Promise<void> {
  const device = deviceId();
  const tabSession = tabSessionId();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const hints = await userAgentHints();
  const noteInput = () => {
    lastInputAt = Date.now();
  };
  for (const type of INPUT_EVENTS) {
    window.addEventListener(type, noteInput, { capture: true, passive: true });
  }

  const beat = () => {
    if (document.visibilityState !== "visible") return;
    const { screen, gameId } = currentScreen();
    void api.activity.heartbeats
      .create({
        body: {
          deviceId: device,
          tabSessionId: tabSession,
          url: `${location.pathname}${location.search}`,
          screen,
          gameId,
          gameLive: gameId !== null && gameId === liveGameId,
          focused: document.hasFocus(),
          inputIdleSeconds:
            lastInputAt === undefined ? null : Math.floor((Date.now() - lastInputAt) / 1000),
          language: navigator.language,
          timezone,
          viewport: { width: window.innerWidth, height: window.innerHeight },
          display: {
            width: window.screen.width,
            height: window.screen.height,
            pixelRatio: window.devicePixelRatio,
          },
          userAgentHints: hints,
        },
      })
      .catch(() => undefined);
  };
  beat();
  setInterval(beat, HEARTBEAT_INTERVAL_SECONDS * 1000);
}
